import {
  SpanStatusCode,
  context,
  trace,
  type Attributes,
  type Context,
  type Tracer,
} from "@opentelemetry/api"
import { SeverityNumber, logs, type LogAttributes, type Logger } from "@opentelemetry/api-logs"
import { resourceFromAttributes, type Resource } from "@opentelemetry/resources"
import {
  BatchSpanProcessor,
  WebTracerProvider,
} from "@opentelemetry/sdk-trace-web"
import { BatchLogRecordProcessor, LoggerProvider } from "@opentelemetry/sdk-logs"
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http"
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http"
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from "@opentelemetry/semantic-conventions"
import { getStoredCompany } from "@/lib/company-storage"
import { httpErrorAttributes } from "@/lib/otel/http-error"

const SERVICE_NAME = "customer-delivery-webpage"

/** Upper bound of one error log body (Loki's default line limit is 256 KB). */
export const LOG_BODY_LIMIT = 192_000

let started = false
let tracer: Tracer = trace.getTracer(SERVICE_NAME)
let logger: Logger = logs.getLogger(SERVICE_NAME)

function parseHeaders(raw: string | undefined): Record<string, string> {
  if (!raw?.trim()) return {}
  const headers: Record<string, string> = {}
  for (const part of raw.split(",")) {
    const idx = part.indexOf("=")
    if (idx <= 0) continue
    const key = part.slice(0, idx).trim()
    const value = part.slice(idx + 1).trim()
    if (key) headers[key] = value
  }
  return headers
}

function buildResource(): Resource {
  return resourceFromAttributes({
    [ATTR_SERVICE_NAME]: SERVICE_NAME,
    [ATTR_SERVICE_VERSION]:
      typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "unknown",
    "deployment.environment": import.meta.env.VITE_DEV_MODE || "unknown",
    "storefront.host": window.location.hostname,
  })
}

/**
 * Boots browser OpenTelemetry (traces + error logs) when `VITE_OTEL_ENABLED=true`
 * and an OTLP HTTP endpoint is configured. Fail-open: never throws into the app boot.
 */
export function initOtel(): void {
  if (started || typeof window === "undefined") return

  const enabled = import.meta.env.VITE_OTEL_ENABLED === "true"
  const endpoint = import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT?.trim()
  if (!enabled || !endpoint) {
    return
  }

  try {
    const base = endpoint.replace(/\/$/, "")
    const headers = parseHeaders(import.meta.env.VITE_OTEL_EXPORTER_OTLP_HEADERS)
    const resource = buildResource()

    const provider = new WebTracerProvider({
      resource,
      spanProcessors: [
        new BatchSpanProcessor(new OTLPTraceExporter({ url: `${base}/v1/traces`, headers }), {
          maxQueueSize: 100,
          maxExportBatchSize: 8,
          scheduledDelayMillis: 2000,
        }),
      ],
    })
    provider.register()
    tracer = trace.getTracer(SERVICE_NAME)

    const loggerProvider = new LoggerProvider({
      resource,
      processors: [
        new BatchLogRecordProcessor({
          exporter: new OTLPLogExporter({ url: `${base}/v1/logs`, headers }),
          maxQueueSize: 50,
          maxExportBatchSize: 4,
          scheduledDelayMillis: 2000,
        }),
      ],
    })
    logs.setGlobalLoggerProvider(loggerProvider)
    logger = logs.getLogger(SERVICE_NAME)
    started = true

    window.addEventListener("unhandledrejection", (event) => {
      const stack = event.reason instanceof Error ? event.reason.stack : undefined
      if (isForeignScriptError({ stack })) {
        emitErrorLog({
          name: "window.foreign_rejection",
          severity: "WARN",
          message: normalizeError(event.reason).message,
          attributes: { "error.frame_url": firstFrameUrl(stack) ?? "" },
          body: { error: describeError(normalizeError(event.reason)) },
        })
        return
      }
      recordException("window.unhandledrejection", event.reason, {
        ...httpErrorAttributes(event.reason),
        ...frameAttributes(stack),
      })
    })
    window.addEventListener("error", (event) => {
      if (isMaskedCrossOriginError(event)) {
        emitErrorLog({
          name: "window.script_error",
          severity: "WARN",
          message: event.message,
          attributes: errorLocationAttributes(event),
        })
        return
      }
      const stack = event.error instanceof Error ? event.error.stack : undefined
      if (isForeignScriptError({ filename: event.filename, stack })) {
        emitErrorLog({
          name: "window.foreign_error",
          severity: "WARN",
          message: event.message,
          attributes: { ...errorLocationAttributes(event), ...frameAttributes(stack) },
          body: { error: describeError(normalizeError(event.error ?? event.message)) },
        })
        return
      }
      recordException("window.error", event.error ?? event.message, {
        ...errorLocationAttributes(event),
        ...frameAttributes(stack),
      })
    })
  } catch (err) {
    console.warn("[otel] init failed (fail-open)", err)
  }
}

export function getTracer(): Tracer {
  return tracer
}

export function isOtelEnabled(): boolean {
  return started
}

export type ErrorLogInput = {
  /** Stable event name (`checkout.fail`, `window.error`, …); lands in Loki as `event_name`. */
  name: string
  /** ERROR (default) feeds the Discord alert; WARN is kept in Loki for counting only. */
  severity?: "ERROR" | "WARN"
  message: string
  /** Scalars only: they become Loki structured metadata and alert labels. */
  attributes?: LogAttributes
  /** Full detail; serialized as the log line (JSON), capped at `LOG_BODY_LIMIT`. */
  body?: Record<string, unknown>
  /** Span to correlate with; Loki stores its trace/span ids as `trace_id` / `span_id`. */
  context?: Context
}

/** Emits one error log record (ERROR unless told otherwise), stamped with page and browser context. No-op until `initOtel` started. */
export function emitErrorLog(input: ErrorLogInput): void {
  if (!started) return
  const severity = input.severity ?? "ERROR"
  try {
    logger.emit({
      severityNumber: severity === "WARN" ? SeverityNumber.WARN : SeverityNumber.ERROR,
      severityText: severity,
      body: serializeBody({ message: input.message, ...(input.body ?? {}) }),
      attributes: {
        "event.name": input.name,
        "error.message": input.message.slice(0, 500),
        ...pageContextAttributes(),
        ...(input.attributes ?? {}),
      },
      context: input.context,
    })
  } catch (err) {
    console.warn("[otel] log emit failed (fail-open)", err)
  }
}

/** Where the customer was and which storefront they are on; no customer PII. */
export function pageContextAttributes(): Attributes {
  if (typeof window === "undefined") return {}
  const attrs: Attributes = {
    "page.path": `${window.location.pathname}${window.location.search}`.slice(0, 300),
    "page.host": window.location.hostname.slice(0, 200),
    "user_agent.original": navigator.userAgent.slice(0, 300),
    "browser.online": navigator.onLine,
  }
  try {
    const company = getStoredCompany()
    if (company?.id) attrs["merchant.id"] = company.id
    if (company?.subdomain) attrs["merchant.subdomain"] = company.subdomain
    if (company?.name && typeof company.name === "string") {
      attrs["merchant.name"] = company.name.slice(0, 120)
    }
  } catch {
    // ignore storage failures
  }
  return attrs
}

/**
 * True for the browser's masked cross-origin error: a script from another
 * origin (pixel, in-app browser, extension) threw and the page only gets the
 * string "Script error." with no location, so there is nothing to act on.
 */
export function isMaskedCrossOriginError(event: ErrorEvent): boolean {
  return !event.error && !event.filename && /^Script error\.?$/.test(event.message ?? "")
}

const OWN_SCRIPT_PATH_PREFIX = "/assets/"

/** First URL that appears in a stack trace (any scheme: https, chrome-extension, blob…), i.e. the script that threw. */
export function firstFrameUrl(stack: string | undefined): string | null {
  if (!stack) return null
  const match = /[a-z][a-z0-9+.-]*:\/\/[^\s()]+?(?=:\d+:\d+|\s|\)|$)/i.exec(stack)
  return match ? match[0] : null
}

/**
 * True when the script that threw is not one we ship. Everything the storefront
 * runs lives under `<origin>/assets/`; an error whose file is the page itself
 * (an in-app browser's injected inline script, e.g. Instagram's
 * `window.webkit.messageHandlers` bridge), another origin (pixel, extension)
 * or a data/blob URL is somebody else's code and never pages us.
 */
export function isForeignScriptError(source: { filename?: string; stack?: string }): boolean {
  const url = source.filename || firstFrameUrl(source.stack)
  if (!url || typeof window === "undefined") return false
  try {
    const parsed = new URL(url, window.location.href)
    return parsed.origin !== window.location.origin || !parsed.pathname.startsWith(OWN_SCRIPT_PATH_PREFIX)
  } catch {
    return false
  }
}

function frameAttributes(stack: string | undefined): Attributes {
  const url = firstFrameUrl(stack)
  return url ? { "error.frame_url": url.slice(0, 300) } : {}
}

/** File, line and column the browser attached to a `window.error` event, when it exposed them. */
export function errorLocationAttributes(event: ErrorEvent): Attributes {
  const attrs: Attributes = {}
  if (event.filename) attrs["error.filename"] = event.filename.slice(0, 300)
  if (event.lineno) attrs["error.lineno"] = event.lineno
  if (event.colno) attrs["error.colno"] = event.colno
  return attrs
}

function serializeBody(body: Record<string, unknown>): string {
  let serialized: string
  try {
    serialized = JSON.stringify(body) ?? ""
  } catch {
    serialized = String(body.message ?? "")
  }
  if (serialized.length <= LOG_BODY_LIMIT) return serialized
  return `${serialized.slice(0, LOG_BODY_LIMIT)}…[truncated ${serialized.length - LOG_BODY_LIMIT} chars]`
}

/** Records a standalone error span plus an ERROR log, for failures that happen outside a checkout span. */
export function recordException(name: string, err: unknown, attrs?: Attributes): void {
  if (!started) return
  const error = normalizeError(err)
  const span = tracer.startSpan(name, { attributes: attrs })
  span.recordException(error)
  span.setStatus({ code: SpanStatusCode.ERROR, message: messageOf(err) })
  emitErrorLog({
    name,
    message: error.message,
    attributes: { ...(attrs ?? {}), "error.type": error.name },
    body: { error: describeError(error) },
    context: trace.setSpan(context.active(), span),
  })
  span.end()
}

export function normalizeError(err: unknown): Error {
  if (err instanceof Error) return err
  return new Error(typeof err === "string" ? err : JSON.stringify(err))
}

/** Name, message and stack of an error, for a log body. */
export function describeError(error: Error): Record<string, unknown> {
  return { name: error.name, message: error.message, stack: error.stack?.slice(0, 4000) }
}

function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === "string") return err
  try {
    return JSON.stringify(err)
  } catch {
    return String(err)
  }
}

export { context, trace }
